/**
 * K-1 / 1065 field-resolution helper.
 *
 * Background: K-1 and 1065 data has been stored under MANY key variants
 * across the history of this codebase.  Without a single reader, each
 * component invented its own `a || b || c || d || 0` fallback chain, and
 * they drifted.  One Guaranteed-Pay column was reading
 * `schedule_k.line_22_guaranteed_payments` — a key that's factually
 * mislabeled (IRS Form 1065 Line 22 is *ordinary business income*, not
 * guaranteed payments; guaranteed payments are Line 10 on Page 1 and
 * Line 4 on Schedule K).  That's how a "$144,000 Guaranteed Pay" column
 * silently rendered ordinary-income values on some claims.
 *
 * Canonical read priority (highest trust first):
 *   1. K-1 partner-level                 → `k1_data.box_N_*`
 *   2. K-1 first partner in schedule_k1  → `schedule_k1.partners[0].box_N_*`
 *   3. 1065 Schedule K (Page 4)          → `schedule_k.line_N_*` (CORRECT line numbers)
 *   4. 1065 Page 1 Income/Deductions     → `page_1.line_N_*`
 *   5. Top-level legacy shorthands       → `ordinary_income`, `guaranteed_payments`, etc.
 *   6. Legacy-mislabeled keys            → `schedule_k.line_22_guaranteed_payments` (actually stores guar. payments)
 *                                        → `k1_data.box_9_rental_income` (actually stores cap gain)
 *
 * The helper tries each path in order and returns the first non-null,
 * non-NaN number.  Callers should use this instead of inline `||`
 * chains so naming drift in one component can't silently spread.
 */

type AnyRecord = Record<string, any> | null | undefined;

const num = (v: unknown): number | undefined => {
  if (v === null || v === undefined) return undefined;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : undefined;
};

/** First non-undefined of a chain of `() => value` lookups. */
const firstOf = (...paths: Array<() => unknown>): number | undefined => {
  for (const p of paths) {
    try {
      const v = num(p());
      if (v !== undefined) return v;
    } catch {
      /* path traversal failure — try next */
    }
  }
  return undefined;
};

/**
 * Resolve a K-1 box value from any supported data shape.
 *
 * @param source - tax_data object (either from uploaded_files.parsed_data.tax_data
 *                 or from draft.data.tax_data entries)
 * @param box    - box key, e.g. 'box_1_ordinary_income' or 'box_4_guaranteed_payments'
 * @returns      - number (including 0) if found anywhere, else undefined
 */
export const resolveK1Box = (source: AnyRecord, box: string): number | undefined => {
  if (!source) return undefined;
  const k1 = source.k1_data as AnyRecord;
  const sk1Partners = source.schedule_k1?.partners as any[] | undefined;
  const firstPartner = Array.isArray(sk1Partners) && sk1Partners.length > 0 ? sk1Partners[0] : undefined;

  return firstOf(
    () => k1?.[box],
    () => firstPartner?.[box],
    () => source[box],
    // Map box → legacy-shorthand aliases
    () => {
      if (box === 'box_1_ordinary_income') return source.ordinary_income;
      if (box === 'box_4_guaranteed_payments') return source.guaranteed_payments;
      if (box === 'box_9a_net_lt_capital_gain') return k1?.box_9_rental_income; // mislabeled legacy — same slot
      return undefined;
    },
    // 1065 schedule_k fallback for cross-validation reads
    () => {
      const sk = source.schedule_k as AnyRecord;
      if (!sk) return undefined;
      if (box === 'box_1_ordinary_income') return sk.line_1_ordinary_income;
      if (box === 'box_4_guaranteed_payments') {
        // Prefer the CORRECTLY-named Schedule K Line 4 field.  Fall back
        // to the legacy-mislabeled `line_22_guaranteed_payments` key
        // ONLY if Line 4 is absent (some old parser outputs wrote the
        // guaranteed-payments value into a Line-22-named slot even
        // though Line 22 is actually ordinary income).
        return sk.line_4_guaranteed_payments ?? sk.line_22_guaranteed_payments;
      }
      return undefined;
    },
  );
};

/**
 * Resolve the 1065-level guaranteed-payments value from a tax_data object.
 *
 * Canonical priority: Schedule K Line 4 → Page 1 Line 10 → top-level
 * shorthand → legacy mislabel.  NEVER read `line_22_*` as guaranteed
 * payments — Line 22 is ordinary income.
 */
export const resolveGuaranteedPayments = (source: AnyRecord): number | undefined => {
  if (!source) return undefined;
  return firstOf(
    () => source.schedule_k?.line_4_guaranteed_payments,
    () => source.page_1?.line_10_guaranteed_payments,
    () => source.guaranteed_payments,
    () => source.k1_data?.box_4_guaranteed_payments,
    () => source.schedule_k1?.partners?.[0]?.box_4_guaranteed_payments,
    () => source.schedule_k?.line_22_guaranteed_payments, // legacy mislabel — last resort
  );
};

/**
 * Resolve the 1065-level ordinary income value.
 *
 * Canonical priority: Page 1 Line 22 → Schedule K Line 1 → top-level.
 */
export const resolveOrdinaryIncome = (source: AnyRecord): number | undefined => {
  if (!source) return undefined;
  return firstOf(
    () => source.page_1?.line_22_ordinary_income,
    () => source.schedule_k?.line_1_ordinary_income,
    () => source.ordinary_income,
    () => source.k1_data?.box_1_ordinary_income,
    () => source.schedule_k1?.partners?.[0]?.box_1_ordinary_income,
  );
};

/**
 * Resolve the partner list for a K-1 record, normalizing both the
 * canonical `schedule_k1.partners[]` shape AND the legacy two-partner
 * hardcoded fields (`partner_a_*` / `partner_b_*`) into a single array.
 *
 * Callers should always iterate the returned array, never read
 * `partner_a_*` directly — those are being phased out.
 */
export interface NormalizedPartner {
  name: string;
  ownership_pct?: number;
  box_1_ordinary_income?: number;
  box_4_guaranteed_payments?: number;
  box_19_distributions?: number;
}

export const resolvePartners = (source: AnyRecord): NormalizedPartner[] => {
  if (!source) return [];
  const partners: NormalizedPartner[] = [];

  // Preferred: canonical schedule_k1.partners[] array
  const arr = source.schedule_k1?.partners;
  if (Array.isArray(arr) && arr.length > 0) {
    for (const p of arr) {
      partners.push({
        name: p?.name || 'Unknown Partner',
        ownership_pct: num(p?.ownership_pct ?? p?.ownership_percent ?? p?.ownership_percentage),
        box_1_ordinary_income: num(p?.box_1_ordinary_income ?? p?.distributive_share),
        box_4_guaranteed_payments: num(p?.box_4_guaranteed_payments),
        box_19_distributions: num(p?.box_19_distributions),
      });
    }
    return partners;
  }

  // Legacy two-partner hardcode fallback — collect partner_a_* + partner_b_*
  // and emit as an array.  Accepts variant spellings (own_pct, ownership__pct,
  // dist vs distribution) to accommodate old CSV-parsed records.
  for (const letter of ['a', 'b'] as const) {
    const pct = num(
      source[`partner_${letter}_ownership_pct`] ??
      source[`partner_${letter}_ownership__pct`] ??
      source[`partner_${letter}_own_pct`] ??
      source[`partner_${letter}_own_`]
    );
    const ord = num(source[`partner_${letter}_ordinary_income`]);
    const guar = num(source[`partner_${letter}_guaranteed_payments`]);
    const dist = num(
      source[`partner_${letter}_distribution`] ??
      source[`partner_${letter}_dist`]
    );
    const hasAny = pct !== undefined || ord !== undefined || guar !== undefined || dist !== undefined;
    if (hasAny) {
      partners.push({
        name: source[`partner_${letter}_name`] || `Partner ${letter.toUpperCase()}`,
        ownership_pct: pct,
        box_1_ordinary_income: ord,
        box_4_guaranteed_payments: guar,
        box_19_distributions: dist,
      });
    }
  }
  return partners;
};

/**
 * Format a K-1 dollar value for table cells.  Zero / missing → em-dash.
 * Negative → parens in red (caller adds the color).
 */
export const formatK1Value = (v: number | undefined | null): string => {
  if (v === undefined || v === null || v === 0 || Number.isNaN(v)) return '\u2014';
  const abs = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return v < 0 ? `($${abs})` : `$${abs}`;
};
